import React, {useState, useEffect} from 'react';
import {profesoresAPI} from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';
import Modal from '../Common/Modal';
import TeacherForm from '../Forms/TeacherForm';
import SearchFilter from '../Common/SearchFilter';
import DataTable from '../Common/DataTable';
import {PencilIcon, TrashIcon, UserPlusIcon, ClipboardDocumentIcon} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {useAuth} from '../../context/AuthContext';
import {copyToClipboard, filterBySearchTerm} from '../../services/utils';

const Teachers = () => {
    const {isReadOnly} = useAuth();
    const [teachers, setTeachers] = useState([]);
    const [filteredTeachers, setFilteredTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchTeachers();
    }, []);

    const fetchTeachers = async () => {
        try {
            setLoading(true);
            const response = await profesoresAPI.getAll();
            setTeachers(response.data);
            setFilteredTeachers(filterTeachersByTerm(response.data, searchTerm));
            setError(null);
        } catch (err) {
            setError('Error al cargar los profesores');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterTeachersByTerm = (teachersData, term) => {
        return filterBySearchTerm(teachersData, term, (teacher, lowerTerm) =>
            teacher.Nombre.toLowerCase().includes(lowerTerm) || teacher.Apellido.toLowerCase().includes(lowerTerm) || teacher.Email.toLowerCase().includes(lowerTerm) || teacher.Telefono.includes(lowerTerm)
        );
    };

    const handleSearch = (searchTerm) => {
        setSearchTerm(searchTerm);
        setFilteredTeachers(filterTeachersByTerm(teachers, searchTerm));
    };

    const handleAdd = () => {
        setSelectedTeacher(null);
        setIsModalOpen(true);
    };

    const handleEdit = (teacher) => {
        setSelectedTeacher(teacher);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar este profesor?')) {
            try {
                await profesoresAPI.delete(id);
                toast.success('Profesor eliminado correctamente');
                fetchTeachers();
            } catch (err) {
                toast.error('Error al eliminar el profesor');
                console.error(err);
            }
        }
    };

    const handleCopyEmails = () => {
        const emails = filteredTeachers
            .map(teacher => teacher.Email)
            .filter(email => email)
            .join('; ');

        if (!emails) {
            toast.error('No hay emails disponibles');
            return;
        }

        copyToClipboard(
            emails,
            () => toast.success('Emails copiados al portapapeles'),
            (error) => toast.error(error || 'Error al copiar los emails')
        );
    };

    const handleSubmit = async (formData) => {
        try {
            if (selectedTeacher) {
                await profesoresAPI.update(selectedTeacher.IDProfesor, formData);
                toast.success('Profesor actualizado correctamente');
            } else {
                await profesoresAPI.create(formData);
                toast.success('Profesor creado correctamente');
            }
            setIsModalOpen(false);
            fetchTeachers();
        } catch (err) {
            toast.error('Error al guardar el profesor');
            console.error(err);
        }
    };

    const columns = [
        {
            key: 'Nombre',
            label: 'Nombre',
            sortable: true
        }, {
            key: 'Apellido',
            label: 'Apellido',
            sortable: true
        }, {
            key: 'Telefono',
            label: 'Teléfono'
        }, {
            key: 'Email',
            label: 'Email'
        }
    ];

    // Agregar columna de acciones solo si no es readonly
    if (!isReadOnly()) {
        columns.push({
            key: 'actions',
            label: 'Acciones',
            render: (_, teacher) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleEdit(teacher)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Editar">
                        <PencilIcon className="h-5 w-5"/>
                    </button>
                    <button
                        onClick={() => handleDelete(teacher.IDProfesor)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar">
                        <TrashIcon className="h-5 w-5"/>
                    </button>
                </div>
            )
        });
    }

    if (loading) 
        return <LoadingSpinner text="Cargando profesores..."/>;
    if (error) 
        return <ErrorMessage message={error} onRetry={fetchTeachers}/>;
    
    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">Profesores</h1>
                <div className="flex space-x-2">
                    <button
                        onClick={handleCopyEmails}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        disabled={filteredTeachers.length === 0}>
                        <ClipboardDocumentIcon className="h-5 w-5 mr-2"/>
                        Copiar Emails
                    </button>
                    {
                        !isReadOnly() && (
                            <button onClick={handleAdd} className="btn-primary">
                                <UserPlusIcon className="h-5 w-5 mr-2"/>
                                Nuevo Profesor
                            </button>
                        )
                    }
                </div>
            </div>

            <SearchFilter
                onSearch={handleSearch}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Buscar por nombre, apellido, email o teléfono..."/>

            <div className="card">
                <DataTable
                    columns={columns}
                    data={filteredTeachers}
                    emptyMessage="No hay profesores registrados"/>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={selectedTeacher
                    ? 'Editar Profesor'
                    : 'Nuevo Profesor'}
                size="md">
                <TeacherForm
                    teacher={selectedTeacher}
                    onSubmit={handleSubmit}
                    onCancel={() => setIsModalOpen(false)}/>
            </Modal>
        </div>
    );
};

export default Teachers;
